import { Box, Typography } from "@mui/material";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import type { VisualizerStand } from "../models/MillVisualizer";
import { PROPERTY_COLUMN_WIDTH } from "../utils/viewBoxLayout";
import { PreheatingFurnace } from "./PreheatingFurnace";

interface Props {
	stands: VisualizerStand[];
}

const standNameSx = {
	fontSize: "13px",
	fontWeight: 700,
	color: mesfColors.text.primary,
	lineHeight: 1.1,
	textAlign: "center",
};

const labelSx = {
	fontSize: "10px",
	color: mesfColors.text.secondary,
	letterSpacing: "0.05em",
	lineHeight: 1,
	fontWeight: 700,
};

const valueSx = {
	fontSize: "12px",
	fontWeight: 600,
	color: mesfColors.text.primary,
	lineHeight: 1.15,
};

const fmt = (n: number | null, dp = 1): string =>
	n == null || !Number.isFinite(n) ? "—" : n.toFixed(dp);

// Derive R-Factor display state. Out-of-band only flagged when the recipe
// has comparison enabled and all bounds + actual are present.
const getRFactorState = (
	stand: VisualizerStand,
): { label: string; color: string; outOfBand: boolean } => {
	const v = stand.rFactorCurrent;
	if (v == null) {
		return { label: "—", color: mesfColors.text.secondary, outOfBand: false };
	}
	const label = v.toFixed(3);
	const canCompare =
		stand.rFactorComparisonEnabled === true &&
		stand.rFactorMin != null &&
		stand.rFactorMax != null;
	if (canCompare) {
		const out =
			v < (stand.rFactorMin as number) || v > (stand.rFactorMax as number);
		return {
			label,
			color: out ? mesfColors.error.main : mesfColors.success.main,
			outOfBand: out,
		};
	}
	return { label, color: mesfColors.text.primary, outOfBand: false };
};

const formatRFactorTitle = (stand: VisualizerStand): string => {
	const t = stand.rFactorTarget;
	const mn = stand.rFactorMin;
	const mx = stand.rFactorMax;
	if (t == null && mn == null && mx == null) return "R-Factor (no recipe band)";
	const target = t == null ? "—" : t.toFixed(3);
	const min = mn == null ? "—" : mn.toFixed(3);
	const max = mx == null ? "—" : mx.toFixed(3);
	return `R-Factor — target ${target}, range ${min} – ${max}`;
};

const KpiCard = ({ stand }: { stand: VisualizerStand }) => {
	if (stand.isCBT) {
		return (
			<Box
				sx={{
					border: `1px dashed ${mesfColors.grey[300]}`,
					borderRadius: 0.5,
					p: 0.75,
					bgcolor: mesfColors.background.paper,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: 100,
					minWidth: 0,
				}}
			>
				<Typography sx={standNameSx}>{stand.name}</Typography>
				<Typography sx={{ ...labelSx, mt: 0.5 }}>cooling</Typography>
			</Box>
		);
	}

	const rfc = getRFactorState(stand);

	return (
		<Box
			sx={{
				border: `1px solid ${mesfColors.grey[200]}`,
				borderRadius: 0.5,
				p: 0.75,
				bgcolor: mesfColors.background.paper,
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
				minWidth: 0,
				minHeight: 100,
			}}
		>
			<Box
				sx={{
					borderBottom: `1px solid ${mesfColors.grey[200]}`,
					pb: 0.3,
					mb: 0.15,
				}}
			>
				<Typography sx={standNameSx}>{stand.name}</Typography>
			</Box>

			{/* RFC — actual R-Factor, color-coded against recipe band */}
			<Box
				title={formatRFactorTitle(stand)}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={labelSx}>RFC</Typography>
				<Typography
					sx={{
						...valueSx,
						color: rfc.color,
						fontWeight: rfc.outOfBand ? 700 : 600,
					}}
				>
					{rfc.label}
				</Typography>
			</Box>

			{/* SPD (units omitted to keep the card responsive — implied m/s) */}
			<Box
				title="Roll-tangential speed (m/s)"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={labelSx}>SPD</Typography>
				<Typography sx={valueSx}>{fmt(stand.speedMps)}</Typography>
			</Box>

			{/* AMP — motor current. Label is the SI unit (Ampere) to match the
			SCADA-style 3-letter labels of RFC and SPD. The grid below uses
			the full word "Current" — same data, different visual register. */}
			<Box
				title="Motor current of the stand (amperes). Higher when the stand is rolling harder."
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={labelSx}>AMP</Typography>
				<Typography sx={valueSx}>{fmt(stand.currentA, 3)}</Typography>
			</Box>

			{/* BIL — BilletId currently assigned to the stand. Useful for
			screenshotting bugs without having to hover the visualizer for
			a tooltip. Synthetic ids (negative) render as "?" so they're
			obvious vs real ids. Empty stand renders "—". */}
			<Box
				title="BilletId currently rolling through this stand (real id from MILL.GetRecentBilletsInFlight, or '?' when not identified)."
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={labelSx}>BIL</Typography>
				<Typography
					sx={{
						...valueSx,
						color:
							stand.currentBilletId == null
								? mesfColors.text.secondary
								: stand.currentBilletId < 0
									? mesfColors.text.secondary
									: mesfColors.text.primary,
					}}
				>
					{stand.currentBilletId == null
						? "—"
						: stand.currentBilletId < 0
							? "?"
							: `#${stand.currentBilletId}`}
				</Typography>
			</Box>
		</Box>
	);
};

export const KpiStrip = ({ stands }: Props) => {
	return (
		<Box
			sx={{
				display: "grid",
				// First column matches the trend grid's pinned-left column so
				// the strip aligns horizontally with the table below.
				gridTemplateColumns: `${PROPERTY_COLUMN_WIDTH}px repeat(${stands.length}, 1fr)`,
				gap: 0,
				width: "100%",
			}}
		>
			<PreheatingFurnace variant="strip" />
			{stands.map((s) => (
				<KpiCard key={s.id} stand={s} />
			))}
		</Box>
	);
};
